// Complete App.js with all pages and routing - Updated

import React, { useState, useEffect } from 'react';

// Layout Components
import Header from './components/layout/Header';   
import Footer from './components/layout/Footer';    

// Main Pages
import HomePage from './pages/HomePage';
import GiftFinderPage from './pages/GiftFinderPage';
import ContactPage from './pages/ContactPage';

// For now, we'll use HomePage as placeholder until the files are properly placed
import CategoriesOverviewPage from './pages/CategoriesOverviewPage';
import OccasionCategoriesPage from './pages/OccasionCategoriesPage';
import RecipientCategoriesPage from './pages/RecipientCategoriesPage';

// Occasion pages
import WeddingPage from './pages/categories/occasion/WeddingPage';
import BirthdayPage from './pages/categories/occasion/BirthdayPage';
import ChristmasPage from './pages/categories/occasion/ChristmasPage';

// Category pages
import ElectronicsPage from './pages/categories/category/ElectronicsPage';

// Price range pages
import Under200Page from './pages/categories/price/Under200Page';
import Page200500 from './pages/categories/price/Page200500';
import Page5001000 from './pages/categories/price/Page5001000';
import Page1000Plus from './pages/categories/price/Page1000Plus';

// Recipient pages
import ForHerPage from './pages/categories/recipient/ForHerPage';
import ForHimPage from './pages/categories/recipient/ForHimPage';
import ForKidsPage from './pages/categories/recipient/ForKidsPage';
import ForTeachersPage from './pages/categories/recipient/ForTeachersPage';
import ForTeensPage from './pages/categories/recipient/ForTeensPage';
import ForGrandparentsPage from './pages/categories/recipient/ForGrandparentsPage';

// SEO and utilities
import SEO from './components/SEO';
import { getSEOConfig, generateBreadcrumbs, generateStructuredDataBreadcrumbs } from './utils/seoConfig';

import './App.css';

// URL routing configuration - moved outside component to prevent ESLint warnings
const urlRouting = {
  // Main pages
  '/': 'home',
  '/gavefinner': 'gift-finder',
  '/kontakt': 'contact',
  
  // Category overview pages
  '/kategorier': 'categories',
  '/anledninger': 'occasions',
  '/mottakere': 'recipients',
  
  // Gift categories - Occasions
  '/gaver/bryllup': 'wedding',
  '/gaver/bursdag': 'birthday', 
  '/gaver/jul': 'christmas',
  
  // Gift categories - Product types
  '/gaver/elektronikk': 'electronics',
  
  // Recipients
  '/mottakere/han': 'for-him',
  '/mottakere/henne': 'for-her',
  '/mottakere/barn': 'for-kids',
  '/mottakere/laerere': 'for-teachers',
  '/mottakere/tenaaringer': 'for-teens',
  '/mottakere/besteforeldre': 'for-grandparents',
  
  // Budget categories
  '/budsjett/under-200': 'under-200',
  '/budsjett/200-500': '200-500',
  '/budsjett/500-1000': '500-1000',
  '/budsjett/over-1000': '1000-plus'
};

// Reverse mapping for navigation - moved outside component
const pageToUrl = {
  'home': '/',
  'gift-finder': '/gavefinner',
  'contact': '/kontakt',
  
  // Category overview pages
  'categories': '/kategorier',
  'occasions': '/anledninger',
  'recipients': '/mottakere',
  
  // Occasions
  'wedding': '/gaver/bryllup',
  'birthday': '/gaver/bursdag',
  'christmas': '/gaver/jul',
  
  // Product categories
  'electronics': '/gaver/elektronikk',
  
  // Recipients
  'for-him': '/mottakere/han',
  'for-her': '/mottakere/henne',
  'for-kids': '/mottakere/barn',
  'for-teachers': '/mottakere/laerere',
  'for-teens': '/mottakere/tenaaringer',
  'for-grandparents': '/mottakere/besteforeldre',
  
  // Budget ranges
  'under-200': '/budsjett/under-200',
  '200-500': '/budsjett/200-500',
  '500-1000': '/budsjett/500-1000',
  '1000-plus': '/budsjett/over-1000'
};

// Navigation items configuration - moved outside component
const navigationItems = [
  {
    id: 'gift-finder',
    name: 'Gavefinner',
    url: '/gavefinner'
  },
  {
    id: 'categories',
    name: 'Gavekategorier',
    subItems: [
      { id: 'wedding', name: 'Bryllupsgaver', icon: '💍', url: '/gaver/bryllup' },
      { id: 'birthday', name: 'Bursdagsgaver', icon: '🎂', url: '/gaver/bursdag' },
      { id: 'christmas', name: 'Julegaver', icon: '🎄', url: '/gaver/jul' },
      { id: 'electronics', name: 'Elektronikk', icon: '📱', url: '/gaver/elektronikk' }
    ]
  },
  {
    id: 'recipients',
    name: 'Mottakere',
    subItems: [
      { id: 'for-him', name: 'Gaver til han', icon: '👨', url: '/mottakere/han' },
      { id: 'for-her', name: 'Gaver til henne', icon: '👩', url: '/mottakere/henne' },
      { id: 'for-kids', name: 'Gaver til barn', icon: '👶', url: '/mottakere/barn' },
      { id: 'for-teachers', name: 'Gaver til lærere', icon: '👩‍🏫', url: '/mottakere/laerere' },
      { id: 'for-teens', name: 'Gaver til tenåringer', icon: '🧒', url: '/mottakere/tenaaringer' },
      { id: 'for-grandparents', name: 'Gaver til besteforeldre', icon: '👴', url: '/mottakere/besteforeldre' }
    ]
  },
  {
    id: 'budget',
    name: 'Budsjett',
    subItems: [
      { id: 'under-200', name: 'Under 200 kr', icon: '💸', url: '/budsjett/under-200' },
      { id: '200-500', name: '200-500 kr', icon: '💎', url: '/budsjett/200-500' },
      { id: '500-1000', name: '500-1000 kr', icon: '👑', url: '/budsjett/500-1000' },
      { id: '1000-plus', name: 'Over 1000 kr', icon: '💍', url: '/budsjett/over-1000' }
    ]
  },
  {
    id: 'contact',
    name: 'Kontakt',
    url: '/kontakt'
  }
];

// Get SEO configuration for current page - moved outside component
const getSEOKey = (pageId) => {
  const seoKeyMapping = {
    // Main pages
    'home': 'home',
    'gift-finder': 'giftFinder',
    'contact': 'contact',
    
    // Category overview pages
    'categories': 'categories',
    'occasions': 'occasions', 
    'recipients': 'recipients',
    
    // Occasions
    'wedding': 'wedding',
    'birthday': 'birthday',
    'christmas': 'christmas',
    
    // Product categories
    'electronics': 'electronics',
    
    // Recipients
    'for-him': 'forHim',
    'for-her': 'forHer',
    'for-kids': 'forKids',
    'for-teachers': 'forTeachers',
    'for-teens': 'forTeens',
    'for-grandparents': 'forGrandparents',
    
    // Budget ranges
    'under-200': 'under200',
    '200-500': 'budget200500',
    '500-1000': 'budget5001000', 
    '1000-plus': 'budget1000Plus'
  };
  return seoKeyMapping[pageId] || 'home';
};

// Helper function to categorize pages for analytics
const getPageCategory = (pageId) => {
  const categoryMap = {
    'home': 'Homepage',
    'gift-finder': 'Gift Finder',
    'contact': 'Contact',
    
    // Occasions
    'wedding': 'Occasions',
    'birthday': 'Occasions', 
    'christmas': 'Occasions',
    
    // Product categories
    'electronics': 'Product Categories',
    
    // Recipients
    'for-him': 'Recipients',
    'for-her': 'Recipients',
    'for-kids': 'Recipients',
    'for-teachers': 'Recipients',
    'for-teens': 'Recipients',
    'for-grandparents': 'Recipients',
    
    // Budget
    'under-200': 'Budget',
    '200-500': 'Budget',
    '500-1000': 'Budget',
    '1000-plus': 'Budget'
  };
  
  return categoryMap[pageId] || 'Other';
};

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [currentPath, setCurrentPath] = useState('/');

  // Initialize routing on mount
  useEffect(() => {
    const path = window.location.pathname;
    const page = urlRouting[path] || 'home';
    setCurrentPage(page);
    setCurrentPath(path);
  }, []); // Clean dependency array - no ESLint warnings

  // Enhanced navigation function with URL updates
const setCurrentPageWithUrl = (pageId) => {
  const url = pageToUrl[pageId] || '/';
  const previousPage = currentPage;
  
  // Update browser URL without page reload
  window.history.pushState({ page: pageId }, '', url);
  
  setCurrentPage(pageId);
  setCurrentPath(url);
  
  // Scroll to top on navigation
  window.scrollTo(0, 0);
  
  // Google Analytics page tracking
  if (window.gtag) {
    // Track page view
    window.gtag('config', 'G-EQFNYNL6E6', {
      page_path: url,
      page_title: document.title
    });
    
    // Track navigation event
    if (window.trackNavigation && previousPage !== pageId) {
      window.trackNavigation(previousPage, pageId);
    }
    
    // Set page category for better segmentation
    const category = getPageCategory(pageId);
    if (window.setPageCategory && category) {
      window.setPageCategory(category);
    }
  }
};

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const path = window.location.pathname;
      const page = urlRouting[path] || 'home';
      setCurrentPage(page);
      setCurrentPath(path);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Clean dependency array - no ESLint warnings

  // Get SEO configuration
  const seoConfig = getSEOConfig(getSEOKey(currentPage));
  const breadcrumbs = generateBreadcrumbs(currentPath);
  const breadcrumbsStructuredData = generateStructuredDataBreadcrumbs(breadcrumbs);

  // Render current page component
  const renderPage = () => {
    switch (currentPage) {
      // Main pages
      case 'home':
        return <HomePage setCurrentPage={setCurrentPageWithUrl} />;
      case 'gift-finder':
        return <GiftFinderPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'contact':
        return <ContactPage setCurrentPage={setCurrentPageWithUrl} />;
      
      // Category overview pages - Using HomePage as placeholder until files are created
      case 'categories':
        return <CategoriesOverviewPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'occasions':
        return <OccasionCategoriesPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'recipients':
        return <RecipientCategoriesPage setCurrentPage={setCurrentPageWithUrl} />;
      
      // Occasion pages
      case 'wedding':
        return <WeddingPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'birthday':
        return <BirthdayPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'christmas':
        return <ChristmasPage setCurrentPage={setCurrentPageWithUrl} />;
      
      // Product category pages
      case 'electronics':
        return <ElectronicsPage setCurrentPage={setCurrentPageWithUrl} />;

        
      // Recipient pages
      case 'for-him':
        return <ForHimPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'for-her':
        return <ForHerPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'for-kids':
        return <ForKidsPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'for-teachers':
        return <ForTeachersPage setCurrentPage={setCurrentPageWithUrl} />;
      case 'for-teens':
        return <ForTeensPage setCurrentPage={setCurrentPageWithUrl} />; // Placeholder until file is created
      case 'for-grandparents':
        return <ForGrandparentsPage setCurrentPage={setCurrentPageWithUrl} />; // Placeholder until file is created
      
      // Budget range pages
      case 'under-200':
        return <Under200Page setCurrentPage={setCurrentPageWithUrl} />; 
      case '200-500':
        return <Page200500 setCurrentPage={setCurrentPageWithUrl} />; // Placeholder until file is created
      case '500-1000':
        return <Page5001000 setCurrentPage={setCurrentPageWithUrl} />; // Placeholder until file is created
      case '1000-plus':
        return <Page1000Plus setCurrentPage={setCurrentPageWithUrl} />; // Placeholder until file is created
      
      // Default fallback
      default:
        return <HomePage setCurrentPage={setCurrentPageWithUrl} />;
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* SEO Component */}
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        keywords={seoConfig.keywords}
        canonicalUrl={seoConfig.canonicalUrl}
        ogImage={seoConfig.ogImage}
        structuredData={seoConfig.structuredData}
        noIndex={seoConfig.noIndex}
      />

      {/* Breadcrumbs Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbsStructuredData)
        }}
      />

      {/* Skip to main content - Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 bg-primary-600 text-white p-2 z-50"
      >
        Hopp til hovedinnhold
      </a>

      <Header 
        currentPage={currentPage}
        setCurrentPage={setCurrentPageWithUrl}
        navigationItems={navigationItems}
        currentPath={currentPath}
      />

      {/* Breadcrumbs Navigation */}
      {breadcrumbs.length > 1 && (
        <nav aria-label="Breadcrumb" className="bg-white border-b border-secondary-200">
          <div className="container mx-auto px-4 py-2">
            <ol className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && <span className="mx-2 text-secondary-400">/</span>}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-secondary-600 font-medium" aria-current="page">
                      {crumb.name}
                    </span>
                  ) : (
                    <button
                      onClick={() => {
                        const pageId = Object.keys(pageToUrl).find(key => pageToUrl[key] === crumb.url) || 'home';
                        setCurrentPageWithUrl(pageId);
                      }}
                      className="text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      {crumb.name}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </nav>
      )}

      <main id="main-content" role="main">
        {renderPage()}
      </main>

      <Footer setCurrentPage={setCurrentPageWithUrl} />
    </div>
  );
};

export default App;